import React, { useState, useEffect } from 'react';
import { NeonButton } from './NeonButton';
import { ThemeName, themes, ThemeService } from '../services/themeService';

interface ThemeSelectorProps {
  onClose: () => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ onClose }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(ThemeService.getCurrentTheme());

  const handleThemeChange = (themeName: ThemeName) => {
    setCurrentTheme(themeName);
    ThemeService.setTheme(themeName);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-retro-dark border-2 border-neon-cyan rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-neon-cyan/30">
          <h2 className="font-retro text-neon-yellow text-xl">
            🎨 SELECCIONAR TEMA
          </h2>
          <button
            onClick={onClose}
            className="ui-button text-neon-pink hover:text-neon-pink/70 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(Object.keys(themes) as ThemeName[]).map((themeName) => {
              const theme = themes[themeName];
              const isSelected = currentTheme === themeName;

              return (
                <button
                  key={themeName}
                  onClick={() => handleThemeChange(themeName)}
                  className={`
                    relative p-4 rounded-lg border-2 transition-all duration-300
                    ${isSelected 
                      ? 'border-neon-green scale-105 shadow-neon-green' 
                      : 'border-gray-600 hover:border-neon-cyan'
                    }
                  `}
                  style={{ 
                    backgroundColor: theme.colors.buttonBg,
                    boxShadow: isSelected ? `0 0 20px ${theme.colors.neonGreen}40` : 'none'
                  }}
                >
                  {isSelected && (
                    <span className="absolute -top-2 -right-2 text-neon-green text-xl">✓</span>
                  )}

                  <div className="flex flex-col items-center gap-3">
                    <div className="flex gap-2">
                      <div 
                        className="w-8 h-8 rounded-full border-2"
                        style={{ 
                          backgroundColor: theme.colors.neonPink,
                          borderColor: theme.colors.neonCyan
                        }}
                      />
                      <div 
                        className="w-8 h-8 rounded-full border-2"
                        style={{ 
                          backgroundColor: theme.colors.neonCyan,
                          borderColor: theme.colors.neonPink
                        }}
                      />
                    </div>
                    
                    <span 
                      className="font-retro text-lg"
                      style={{ 
                        color: theme.colors.neonYellow,
                        textShadow: theme.hasGlow ? `0 0 10px ${theme.colors.neonPink}` : 'none'
                      }}
                    >
                      {theme.label}
                    </span>

                    <div className="flex flex-wrap justify-center gap-1">
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: theme.colors.neonPink + '30',
                          color: theme.colors.neonPink,
                          fontFamily: theme.font
                        }}
                      >
                        A
                      </span>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: theme.colors.neonCyan + '30',
                          color: theme.colors.neonCyan,
                          fontFamily: theme.font
                        }}
                      >
                        a
                      </span>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: theme.colors.neonGreen + '30',
                          color: theme.colors.neonGreen,
                          fontFamily: theme.font
                        }}
                      >
                        1
                      </span>
                    </div>

                    <div className="flex gap-1 text-xs text-gray-400">
                      {theme.hasGlow && <span>✨</span>}
                      {theme.hasScanlines && <span>📺</span>}
                      <span className="ml-1">{theme.font.split(',')[0]}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-retro-black/50 rounded-lg border border-neon-cyan/20">
            <h3 className="font-retro text-neon-cyan text-sm mb-2">
              Vista previa del tablero:
            </h3>
            <div 
              className="grid grid-cols-3 gap-2 p-4 rounded-lg"
              style={{ backgroundColor: themes[currentTheme].colors.background }}
            >
              {['X', null, 'O', null, 'X', null, 'O', null, null].map((cell, i) => (
                <div
                  key={i}
                  className={`
                    h-12 rounded flex items-center justify-center text-2xl font-bold
                    border-2 transition-all duration-300
                  `}
                  style={{
                    backgroundColor: themes[currentTheme].colors.buttonBg,
                    borderColor: themes[currentTheme].colors.gridLines,
                    color: cell === 'X' 
                      ? themes[currentTheme].colors.neonPink 
                      : cell === 'O'
                        ? themes[currentTheme].colors.neonCyan
                        : 'transparent',
                    textShadow: themes[currentTheme].hasGlow && cell 
                      ? `0 0 10px ${cell === 'X' ? themes[currentTheme].colors.neonPink : themes[currentTheme].colors.neonCyan}`
                      : 'none'
                  }}
                >
                  {cell || ''}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-neon-cyan/30 flex justify-center">
          <NeonButton onClick={onClose} color="cyan" className="text-lg">
            APLICAR
          </NeonButton>
        </div>
      </div>
    </div>
  );
};

export default ThemeSelector;
